import { useEffect, useState } from "react";
import axios from "axios";

/**
 * Load a media file that sits behind authentication, and hand back a URL an
 * <img>/<video>/<a> can actually use.
 *
 * Needed because a bare `src="/whatsapp_api/media/..."` fails twice over: the
 * path is root-relative, so the browser sends it to the React origin rather
 * than to `REACT_APP_BASE_URL` (nginx routes "/" to the node server and there
 * is no CRA proxy), and a browser-issued media request carries no
 * Authorization header, while the proxy endpoint requires one. Going through
 * axios fixes both at once — it already holds the base URL, the token and the
 * organization header.
 *
 * Object URLs are cached per path so remounting a thread does not refetch
 * every image, and revoked when the cache entry is the last one out.
 */

const cache = new Map();
const inFlight = new Map();

export const loadAuthedMedia = (path) => load(path);

const load = (path) => {
  if (cache.has(path)) return Promise.resolve(cache.get(path));
  if (inFlight.has(path)) return inFlight.get(path);

  const request = axios
    .get(path, { responseType: "blob" })
    .then((response) => {
      const objectUrl = URL.createObjectURL(response.data);
      cache.set(path, objectUrl);
      inFlight.delete(path);
      return objectUrl;
    })
    .catch((error) => {
      inFlight.delete(path);
      throw error;
    });

  inFlight.set(path, request);
  return request;
};

const useAuthedMedia = (path) => {
  const [state, setState] = useState(() => ({
    url: path ? cache.get(path) || null : null,
    loading: Boolean(path) && !cache.has(path),
    error: null,
  }));

  useEffect(() => {
    if (!path) {
      setState({ url: null, loading: false, error: null });
      return undefined;
    }
    if (cache.has(path)) {
      setState({ url: cache.get(path), loading: false, error: null });
      return undefined;
    }

    let active = true;
    setState({ url: null, loading: true, error: null });
    load(path)
      .then((url) => {
        if (active) setState({ url, loading: false, error: null });
      })
      .catch((error) => {
        if (active) setState({ url: null, loading: false, error });
      });

    return () => {
      active = false;
    };
  }, [path]);

  return state;
};

/** Fetch the same file as a download, keeping the original filename. */
export const downloadAuthed = async (path, filename) => {
  const response = await axios.get(path, { responseType: "blob" });
  const objectUrl = URL.createObjectURL(response.data);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = filename || "download";
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  // Give the click a tick to start before the URL stops resolving.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
};

export default useAuthedMedia;
